import React, { useState, useEffect } from 'react';
import { Box, Typography, Tab, Tabs, IconButton } from '@mui/material';
import './components/seat-one/seatOneOverrides.css';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import GridViewIcon from '@mui/icons-material/GridView';
import TimelineIcon from '@mui/icons-material/Timeline';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import InsightsIcon from '@mui/icons-material/Insights';
import { useSearchParams } from 'react-router-dom';
import AssignmentView from './components/seat-one/AssignmentView';
import TimelineView from './components/seat-one/TimelineView';
import GateView from './components/seat-one/GateView';
import IntelligenceView from './components/seat-one/IntelligenceView';
import { C } from './components/seat-one/styles';

const VALID_VIEWS = new Set(['assignment', 'timeline', 'gates', 'intelligence']);

function SeatOnePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('view');
  const initialView = VALID_VIEWS.has(requested) ? requested : 'assignment';
  const [view, setView] = useState(initialView);

  // Suppress global theme overlays (radial washes, scanlines, etc.)
  // while this page is mounted — see seatOneOverrides.css.
  useEffect(() => {
    document.body.classList.add('seat-one-active');
    return () => document.body.classList.remove('seat-one-active');
  }, []);

  const isPoppedOut = !!searchParams.get('view');

  const handleTabChange = (_, next) => {
    setView(next);
    setSearchParams({}, { replace: true });
  };

  const popOut = (which) => {
    const url = `${window.location.pathname}?view=${which}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box sx={{
      height: 'calc(100vh - 64px)',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      color: C.textPrimary,
      // Flat dark background — masks the global body radial-gradient.
      backgroundColor: '#0a0d14',
      // Constrain the page so wide inner content (timeline/gate lanes)
      // is handled by the inner scroll regions, not by the page itself.
      overflow: 'hidden',
    }}>
      {!isPoppedOut && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: `1px solid ${C.border}`,
          backgroundColor: C.surface,
          backdropFilter: C.blur,
          WebkitBackdropFilter: C.blur,
          px: 2,
        }}>
          <Box sx={{ pr: 3 }}>
            <Typography sx={{ fontSize: '0.65rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}>
              Dispatcher · Seat 1
            </Typography>
            <Typography sx={{ fontSize: '1.1rem', color: C.accent, fontWeight: 700, lineHeight: 1.1 }}>
              Demo
            </Typography>
          </Box>
          <Tabs
            value={view}
            onChange={handleTabChange}
            sx={{
              flex: 1,
              '& .MuiTab-root': {
                color: C.textMuted,
                textTransform: 'none',
                fontSize: '0.85rem',
                fontWeight: 600,
                minHeight: 56,
              },
              '& .Mui-selected': { color: `${C.accent} !important` },
              '& .MuiTabs-indicator': { backgroundColor: C.accent, height: 3 },
            }}
          >
            <Tab icon={<GridViewIcon sx={{ fontSize: 18 }} />}        iconPosition="start" label="Assignment"   value="assignment" />
            <Tab icon={<TimelineIcon sx={{ fontSize: 18 }} />}        iconPosition="start" label="Timeline"     value="timeline" />
            <Tab icon={<FlightTakeoffIcon sx={{ fontSize: 18 }} />}   iconPosition="start" label="Gates"        value="gates" />
            <Tab icon={<InsightsIcon sx={{ fontSize: 18 }} />}        iconPosition="start" label="Intelligence" value="intelligence" />
          </Tabs>
          <IconButton
            onClick={() => popOut(view)}
            title="Pop out into its own window for a second monitor"
            sx={{ color: C.textMuted, '&:hover': { color: C.accent } }}
          >
            <OpenInNewIcon />
          </IconButton>
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {view === 'assignment'   && <AssignmentView />}
        {view === 'timeline'     && <TimelineView />}
        {view === 'gates'        && <GateView />}
        {view === 'intelligence' && <IntelligenceView />}
      </Box>
    </Box>
  );
}

export default SeatOnePage;
